export const BOOK_STEPS = [
  { id: 'protection', label: 'Protection' },
  { id: 'addons', label: 'Add-ons' },
  { id: 'appointment', label: 'Appointment' },
  { id: 'deposit', label: 'Deposit' },
  { id: 'confirmation', label: 'Confirmation' },
] as const

export type BookStepId = (typeof BOOK_STEPS)[number]['id']

export { CERAMIC_BOOKING_DEPOSIT_CENTS as FUTURE_BOOKING_DEPOSIT_CENTS } from './booking-domain.ts'
export const BOOKING_DEPOSIT_AVAILABLE = true
export const APPOINTMENT_SCHEDULING_AVAILABLE = true

export const ADD_ON_NONE_ID = 'no-additional-protection'

export type AddOnChoice = 'unset' | 'none' | 'selected'

export type CheckoutReturnKind = 'return' | 'cancelled' | null

export interface CanonicalBookState {
  protectionId?: string | null
  addOnsCommitted: boolean
  appointmentReserved: boolean
  reservationExpired: boolean
  paymentPaid: boolean
  appointmentConfirmed: boolean
  checkout?: CheckoutReturnKind
}

export interface BookingIdentityInput {
  publicReference?: string | null
  existingPackageId?: string | null
  nextPackageId: string
  paymentPaid?: boolean
  appointmentConfirmed?: boolean
  appointmentReserved?: boolean
  newBookingIntent: boolean
}

export function isBookStepId(value: string): value is BookStepId {
  return BOOK_STEPS.some((step) => step.id === value)
}

export function bookStepIndex(step: BookStepId) {
  return BOOK_STEPS.findIndex((item) => item.id === step)
}

export function nextBookStep(step: BookStepId): BookStepId | null {
  return BOOK_STEPS[bookStepIndex(step) + 1]?.id ?? null
}

export function previousBookStep(step: BookStepId): BookStepId | null {
  return BOOK_STEPS[bookStepIndex(step) - 1]?.id ?? null
}

export function canLeaveProtectionStep(protectionId?: string | null) {
  return Boolean(protectionId)
}

/** Zero selected add-ons is a valid completed choice. */
export function canLeaveAddOnStep(_choice?: AddOnChoice) {
  return true
}

export function isAppointmentConfirmed(status?: string | null) {
  return status === 'confirmed'
}

export function isAppointmentReserved(status?: string | null) {
  return status === 'reserved'
}

/**
 * Furthest step allowed from trusted persisted state.
 * Viewport width is not an input and must never change this result.
 *
 * 1 protection → 2 add-ons → 3 appointment → 4 deposit → 5 confirmation
 */
export function furthestBookStep(state: CanonicalBookState): BookStepId {
  if (!canLeaveProtectionStep(state.protectionId)) return 'protection'
  if (state.paymentPaid && state.appointmentConfirmed) return 'confirmation'
  if (state.paymentPaid && !state.appointmentConfirmed) return 'appointment'
  if (state.reservationExpired) return 'appointment'
  if (state.appointmentReserved) return 'deposit'
  if (state.checkout === 'cancelled' || state.checkout === 'return') {
    return state.appointmentReserved ? 'deposit' : 'appointment'
  }
  if (state.addOnsCommitted) return 'appointment'
  return 'addons'
}

export function resolveCanonicalBookStep(state: CanonicalBookState): BookStepId {
  return furthestBookStep(state)
}

export function clampBookStep(requested: BookStepId, state: CanonicalBookState): BookStepId {
  const furthest = furthestBookStep(state)

  if (state.paymentPaid && (requested === 'protection' || requested === 'addons')) {
    return furthest
  }

  if (bookStepIndex(requested) > bookStepIndex(furthest)) return furthest
  return requested
}

export function shouldRotateBookingIdentity(input: BookingIdentityInput) {
  if (!input.publicReference) return false
  if (input.newBookingIntent) return true
  if (
    (input.paymentPaid || input.appointmentConfirmed || input.appointmentReserved)
    && input.existingPackageId !== input.nextPackageId
  ) {
    return true
  }
  return false
}

export function initialCustomerBookStep(protectionId?: string | null): BookStepId {
  return furthestBookStep({
    protectionId,
    addOnsCommitted: false,
    appointmentReserved: false,
    reservationExpired: false,
    paymentPaid: false,
    appointmentConfirmed: false,
  })
}

export { canEnterConfirmationStep, canLeaveDepositStep } from './deposit-checkout.ts'
